import { Accordion, Field, Input } from '@chakra-ui/react'
import { useStore } from '@tanstack/react-form'
import { FormErrors } from 'commons/components/layout/FormErrors'
import { Label } from 'commons/components/layout/Label'
import { Section } from 'commons/components/layout/Section'
import { MOTIF_MAX_LENGTH } from 'commons/constants/motif'
import { useFieldContext, useFormContext } from 'commons/config/form'
import { BgColors, TextColors } from 'commons/design-system/tokens'
import { toNumber } from 'commons/utils/amountHelpers'
import type { ChangeEvent } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export const MotifSection = () => {
	const field = useFieldContext<string>()
	const form = useFormContext()
	const motif = useRef<HTMLInputElement>(null)
	const amount = useStore(form.store, (state) => toNumber(state.values.amount) || 0)
	const [value, setValue] = useState<string[] | undefined>(field.state.value ? ['0'] : undefined)
	const { t } = useTranslation()

	const handleChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value),
		[field]
	)

	const handleValueChange = useCallback(
		(e: { value: string[] }) => {
			if (amount >= 25) return // Prevent closing when amount >= 25
			setValue(e.value)
		},
		[amount]
	)

	/** If amount >= 25, then open the accordion */
	useEffect(() => {
		if (amount >= 25) {
			setValue(['0'])
		}
	}, [amount])

	return (
		<Field.Root invalid={!field.state.meta.isValid}>
			<Section
				_focusWithin={{ outline: `2px solid ${BgColors.OPPOSITE_Z3}` }}
				_groupInvalid={{
					outline: `2px solid ${TextColors.STATUS_ERROR}`
				}}
			>
				{/* If amount >= 25, can't close the accordion (collapsible=false) */}
				<Accordion.Root collapsible={amount < 25} value={value} onValueChange={handleValueChange}>
					<Accordion.Item border={0} value={'0'}>
						<Accordion.ItemTrigger
							display='flex'
							justifyContent='space-between'
							p={0}
							cursor='pointer'
							_hover={{ bg: BgColors.Z5 }}
							_focus={{ outline: 0 }}
							_focusVisible={{ outline: 0 }}
						>
							<Label>{t('phone_form.reason_label')}</Label>
							<Accordion.ItemIndicator
								color={TextColors.QUATERNARY}
								display={amount >= 25 ? 'none' : 'block'}
							/>
						</Accordion.ItemTrigger>
						<Accordion.ItemContent p={0}>
							<Accordion.ItemBody p={0}>
								<Input
									fontFamily="'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif"
									fontWeight={500}
									type='text'
									variant='unstyled'
									py={2}
									maxLength={MOTIF_MAX_LENGTH}
									placeholder={t('phone_form.reason_placeholder')}
									ref={motif}
									value={field.state.value}
									onChange={handleChange}
								/>
								<FormErrors errors={field.state.meta.errors} />
							</Accordion.ItemBody>
						</Accordion.ItemContent>
					</Accordion.Item>
				</Accordion.Root>
			</Section>
		</Field.Root>
	)
}
